import type { Offer } from "../config/offers";
import type { Faq } from "../seo/schema";

export type MoneyPageContent = {
  offerId: Offer["id"];
  seoTitle: string;
  seoDescription: string;
  eyebrow: string;
  h1: string;
  heroSub: string;
  serviceType: string;
  includedHeading: string;
  pricingHeading: string;
  pricingBody: string;
  whoHeading: string;
  whoLinks: readonly { label: string; href: string }[];
  faqHeading: string;
  faqs: readonly Faq[];
  finalHeadline: string;
  finalBody: string;
};

export const moneyPages: readonly MoneyPageContent[] = [
  {
    offerId: "local-trust-pack",
    seoTitle: "Google Review & Reputation Management — $149/mo, No Contract | Foxmark",
    seoDescription:
      "Done-for-you Google review and reputation management for local businesses. Brand-voice review responses, Google Business Profile posts, and a monthly trust report for a flat $149/month. No contract.",
    eyebrow: "Google review & reputation management",
    h1: "Google review and reputation management for local businesses",
    heroSub:
      "Turn your reviews and Google Business Profile into an active trust signal. Every review answered in your brand voice, four profile posts a month, and a clear monthly trust report — so customers choose you before they call someone else.",
    serviceType: "Online reputation and Google Business Profile management",
    includedHeading: "What's included every month",
    pricingHeading: "Reputation management pricing",
    pricingBody:
      "$149/month, flat. No setup fees, no annual contract, and no per-review pricing. Most reputation management services hide their cost behind a sales call — Foxmark doesn't. One price covers your review responses, Google Business Profile posts, and monthly trust report. Cancel anytime.",
    whoHeading: "Who Google review management is for",
    whoLinks: [
      { label: "Reputation management for dentists", href: "/reputation-management-for-dentists" },
      { label: "Marketing for med spas", href: "/marketing-for-med-spas" },
      { label: "Local SEO for contractors", href: "/local-seo-for-contractors" },
      { label: "SEO for restaurants", href: "/seo-for-restaurants" }
    ],
    faqHeading: "Google review management FAQ",
    faqs: [
      {
        question: "How much does reputation management cost?",
        answer:
          "Foxmark's Google review and reputation management is a flat $149 per month. There are no setup fees, no annual contract, and no per-review charges — one price covers review responses, Google Business Profile posts, and your monthly trust report."
      },
      {
        question: "Do you respond to my Google reviews for me?",
        answer:
          "Yes. Every new review is answered in your brand voice, drafted for your approval or published for you — so customers and Google both see an active, responsive business."
      },
      {
        question: "What is a Google Business Profile post?",
        answer:
          "A Google Business Profile post is an update — an offer, highlight, or answer to a common question — that appears on your Google listing. Foxmark prepares four each month to keep your profile looking active in local search."
      },
      {
        question: "Will this help me get more Google reviews?",
        answer:
          "It focuses on responding to and showcasing the reviews you already have, which encourages more. For a step-by-step approach, see our guide on how to get more Google reviews."
      },
      {
        question: "Is there a contract?",
        answer: "No. Reputation management is month-to-month and you can cancel anytime."
      }
    ],
    finalHeadline: "Put your reviews to work this month",
    finalBody:
      "Start Google review and reputation management today, or review the full website audit service if you also want your site checked for conversion leaks every month."
  },
  {
    offerId: "site-leak-report",
    seoTitle: "Monthly Website Audit Service — Find the Leaks Costing You Leads | $149/mo",
    seoDescription:
      "A monthly website audit service that finds the conversion leaks costing you calls, bookings, and form fills — with plain-English fixes and a 30-day action plan. Flat $149/month, no contract.",
    eyebrow: "Website audit & conversion service",
    h1: "Monthly website audit service that finds what's costing you leads",
    heroSub:
      "Every month Foxmark reviews your website for the trust, clarity, and conversion leaks losing you calls, bookings, and form fills — then hands you a ranked, plain-English fix list and a 30-day action plan.",
    serviceType: "Website conversion audit",
    includedHeading: "What the website audit covers",
    pricingHeading: "Website audit pricing",
    pricingBody:
      "$149/month, flat. A one-time agency audit or a conversion rate optimization retainer can run thousands of dollars. Foxmark gives you a focused website conversion audit every month for one transparent price — no contract, no login required, cancel anytime.",
    whoHeading: "Who the website audit is for",
    whoLinks: [
      { label: "Local SEO for contractors", href: "/local-seo-for-contractors" },
      { label: "Reputation management for dentists", href: "/reputation-management-for-dentists" },
      { label: "SEO for restaurants", href: "/seo-for-restaurants" },
      { label: "Marketing for med spas", href: "/marketing-for-med-spas" }
    ],
    faqHeading: "Website audit FAQ",
    faqs: [
      {
        question: "How much does a website audit cost?",
        answer:
          "Foxmark's monthly website audit service is a flat $149 per month. A one-time agency audit often costs several hundred to several thousand dollars; this gives you a fresh conversion audit every month for one price, with no contract."
      },
      {
        question: "Is this a one-time audit or ongoing?",
        answer:
          "It's a monthly website audit service. Websites, offers, and competitors change, so Foxmark reviews your site every month and hands you a fresh, ranked list of conversion leaks to fix."
      },
      {
        question: "How is this different from a conversion rate optimization agency?",
        answer:
          "A CRO agency typically bills a large monthly retainer to run tests. Foxmark focuses on finding and prioritizing the specific leaks costing you leads, then explaining the fixes in plain English so your team or web person can act on them."
      },
      {
        question: "Do you need access to my website?",
        answer: "No login is required. Foxmark reviews your public, customer-facing website — the same thing your visitors see."
      },
      {
        question: "What do I actually receive?",
        answer:
          "A ranked list of your most important conversion leaks, a plain-English explanation and specific fix for each, and a focused 30-day action plan prioritizing calls, forms, bookings, local visibility, trust signals, and mobile usability."
      }
    ],
    finalHeadline: "Find the leaks costing you leads",
    finalBody:
      "Start your monthly website audit today, or explore Google review and reputation management if you also want your reviews and profile handled every month."
  }
];
